import { APP_BOOTSTRAP_LISTENER, PLATFORM_ID, Provider, inject, ɵJSACTION_BLOCK_ELEMENT_MAP } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * OB-7718 фаза 7: затычка утечки withEventReplay (Angular 22.0.5).
 *
 * withEventReplay ставит stash-функцию на ВСЁ время жизни приложения:
 * каждая регистрация DOM-слушателя (любая страница, любая навигация) кладёт
 * замыкание в el.__jsaction_fns и сам элемент — в JSACTION_BLOCK_ELEMENT_MAP
 * под ключом ''. Штатная чистка этого ключа выполняется РОВНО ОДИН РАЗ
 * (appRef.whenStable после бутстрапа). Всё, что создано после первой
 * стабильности, копится в Map навсегда: отсоединённые DOM-поддеревья
 * уничтоженных страниц + замыкания слушателей + инстансы компонентов
 * (~0.6МБ на SPA-навигацию, подтверждено heap-снапшотами: retainer-путь
 * Set→Map→scope→getAngularTestability).
 *
 * Лечение: после каждой NavigationEnd выкидываем из карты элементы,
 * отвязанные от документа. Дегидрированные @defer-блоки incremental
 * hydration не задеваются — их элементы остаются в живом DOM (isConnected)
 * до момента гидратации; реплей первички тоже цел (чистим только после
 * навигаций, к этому моменту он давно отработал).
 */
export function provideJsactionPrune(): Provider {
    return {
        provide: APP_BOOTSTRAP_LISTENER,
        multi: true,
        useFactory: () => {
            const pid = inject(PLATFORM_ID);
            const map = inject(ɵJSACTION_BLOCK_ELEMENT_MAP);
            const router = inject(Router);
            return () => {
                if (!isPlatformBrowser(pid)) {
                    return;
                }
                // OB-8064. ActionResolver кэширует разбор атрибута в el.__jsaction
                // (replay mouseover, если курсор стоял на ссылке при загрузке:
                // {click: ""}). removeListeners снимает атрибут jsaction и
                // __jsaction_fns, кэш не трогает — дефект Angular 22.0.5.
                // withIncrementalHydration не вызывает eventContract.cleanUp()
                // до уничтожения приложения, и Dispatcher на каждый клик по
                // такому <a> делает preventDefault: вкладка не открывается
                // до перезагрузки. То же у @defer (removeListenersFromBlocks).
                // Мутаций атрибута единицы. Наблюдатель стоит с бутстрапа —
                // раньше whenStable, где атрибут снимают в первый раз.
                new MutationObserver(mutations => {
                    for (const mutation of mutations) {
                        const el = mutation.target as Element;
                        if (!el.getAttribute('jsaction')) {
                            delete (el as any).__jsaction;
                        }
                    }
                }).observe(document.body, {
                    attributes: true,
                    attributeFilter: ['jsaction'],
                    subtree: true,
                });
                (window as any)['obJsactPrune'] = 0; // маркер сборки/диагностики
                router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
                    // после уничтожения компонентов старой страницы
                    setTimeout(() => {
                        let n = 0;
                        for (const [key, set] of map) {
                            for (const el of set) {
                                if (!el.isConnected) {
                                    set.delete(el);
                                    (el as any).__jsaction_fns = undefined;
                                    n++;
                                }
                            }
                            if (!set.size) {
                                map.delete(key);
                            }
                        }
                        (window as any)['obJsactPrune'] += n;
                    });
                });
            };
        }
    };
}
